import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { SERVICE_TYPES } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import { useGeoLocate } from "@/components/LocationFilter";
import { LocateFixed, MapPin } from "lucide-react";

const inputCls = "w-full px-4 min-h-[52px] rounded-xl border border-[#E2D9CE] focus:border-[#D96B43] focus:ring-2 focus:ring-[#D96B43]/20 outline-none";

export default function PostRequest() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { locate, locating } = useGeoLocate();
  const [form, setForm] = useState({
    title: "", service_type: "errands", description: "", location: user.address || "",
    scheduled_date: "", urgency: "normal", payment_type: "voluntary", amount: 0,
    lat: user.lat ?? null, lng: user.lng ?? null, scheduled_at: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setWhen = (e) => {
    const v = e.target.value;
    const label = v ? new Date(v).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "";
    setForm({ ...form, scheduled_at: v, scheduled_date: label });
  };
  const pin = async () => { const c = await locate(); if (c) setForm({ ...form, ...c }); };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, amount: form.payment_type === "paid" ? parseFloat(form.amount || 0) : 0, scheduled_at: form.scheduled_at || null };
      const { data } = await api.post("/requests", payload);
      toast.success("Your request has been posted!");
      navigate(`/requests/${data.id}`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const toggle = (field, val) => () => setForm({ ...form, [field]: val });

  return (
    <div className="max-w-2xl mx-auto px-4 py-10">
      <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900">Post a request for help</h1>
      <p className="text-lg text-slate-600 mt-1 mb-8">Tell us what you need. A caring helper will reach out.</p>

      <form onSubmit={submit} className="bg-white border border-[#E2D9CE] rounded-2xl p-6 sm:p-8 space-y-6 shadow-sm">
        <div>
          <label className="block font-semibold text-slate-800 mb-2">What do you need help with?</label>
          <input data-testid="request-title" required value={form.title} onChange={set("title")} placeholder="e.g. Ride to the eye clinic on Tuesday" className={inputCls} />
        </div>

        <div>
          <label className="block font-semibold text-slate-800 mb-2">Type of service</label>
          <select data-testid="service-type-select" value={form.service_type} onChange={set("service_type")} className={inputCls}>
            {SERVICE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        <div>
          <label className="block font-semibold text-slate-800 mb-2">Details</label>
          <textarea data-testid="request-description" required rows={4} value={form.description} onChange={set("description")}
            placeholder="Share anything the helper should know…" className={`${inputCls} py-3`} />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block font-semibold text-slate-800 mb-2">Location</label>
            <input data-testid="request-location" required value={form.location} onChange={set("location")} placeholder="Neighbourhood / city" className={inputCls} />
            <button type="button" data-testid="request-pin-location-btn" onClick={pin} disabled={locating}
              className="mt-2 flex items-center gap-2 text-[#2E7D6B] font-semibold min-h-[44px] disabled:opacity-60">
              {form.lat != null ? <><MapPin className="w-5 h-5" /> Location pinned</> : <><LocateFixed className="w-5 h-5" /> {locating ? "Locating…" : "Pin my location for nearby helpers"}</>}
            </button>
          </div>
          <div>
            <label className="block font-semibold text-slate-800 mb-2">When</label>
            <input data-testid="request-datetime" type="datetime-local" required value={form.scheduled_at} onChange={setWhen} className={inputCls} />
            <p className="text-sm text-slate-500 mt-1">We'll show you helpers who are free at this time.</p>
          </div>
        </div>

        <div>
          <label className="block font-semibold text-slate-800 mb-2">Urgency</label>
          <div className="flex gap-3">
            {[["normal", "Normal"], ["urgent", "Urgent"]].map(([v, l]) => (
              <button type="button" key={v} data-testid={`urgency-${v}`} onClick={toggle("urgency", v)}
                className={`flex-1 min-h-[48px] rounded-xl border-2 font-semibold transition-all ${
                  form.urgency === v ? "border-[#D96B43] bg-[#FBEBE4] text-[#C25832]" : "border-[#E2D9CE] bg-white text-slate-700"
                }`}>{l}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="block font-semibold text-slate-800 mb-2">Payment</label>
          <div className="flex gap-3">
            {[["voluntary", "Voluntary (free)"], ["paid", "Paid"]].map(([v, l]) => (
              <button type="button" key={v} data-testid={`payment-type-${v}`} onClick={toggle("payment_type", v)}
                className={`flex-1 min-h-[48px] rounded-xl border-2 font-semibold transition-all ${
                  form.payment_type === v ? "border-[#2E7D6B] bg-[#E8F4F1] text-[#2E7D6B]" : "border-[#E2D9CE] bg-white text-slate-700"
                }`}>{l}</button>
            ))}
          </div>
        </div>

        {form.payment_type === "paid" && (
          <div>
            <label className="block font-semibold text-slate-800 mb-2">Amount offered (USD)</label>
            <input data-testid="request-amount" type="number" min="1" step="1" value={form.amount} onChange={set("amount")} className={inputCls} />
          </div>
        )}

        <button data-testid="submit-request-btn" disabled={loading}
          className="w-full bg-[#D96B43] hover:bg-[#C25832] disabled:opacity-60 text-white font-semibold min-h-[52px] rounded-xl text-lg transition-colors">
          {loading ? "Posting…" : "Post request"}
        </button>
      </form>
    </div>
  );
}
